import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";
import { ToggleGroup, ToggleGroupItem } from "@/shared/ui/toggle-group";

import { MAP_LAYERS, type MapLayer } from "../../map/MAP_LAYERS";

const LABEL = msg({ id: "analysis.map.layers", message: "What to show on the map" });

const LAYERS: Record<MapLayer, { label: MessageDescriptor; dot: string }> = {
  mates: {
    label: msg({ id: "analysis.map.layer.mates", message: "Teammates" }),
    dot: "bg-team-amber",
  },
  enemies: {
    label: msg({ id: "analysis.map.layer.enemies", message: "Enemies" }),
    dot: "bg-team-sapphire",
  },
  deaths: {
    label: msg({ id: "analysis.map.layer.other_deaths", message: "Other deaths" }),
    dot: "bg-neutral",
  },
  runes: {
    label: msg({ id: "analysis.map.layer.bridge_runes", message: "Runes" }),
    dot: "bg-map-kill",
  },
  boss: { label: msg({ id: "analysis.map.layer.boss", message: "Mid-boss" }), dot: "bg-map-boss" },
};

type MapLayerTogglesProps = {
  layers: Readonly<Record<MapLayer, boolean>>;
  // Своя команда — первым цветом: у команды 1 «союзники» синие, а «враги» оранжевые.
  team: number;
  onChange: (layers: Record<MapLayer, boolean>) => void;
};

// Слои карты: что показать поверх тепла. На узком экране листаются строкой.
export const MapLayerToggles = ({ layers, team, onChange }: MapLayerTogglesProps) => {
  const { _ } = useLingui();
  const ids = Object.keys(MAP_LAYERS) as MapLayer[];
  const teamDots =
    team === 0 ? ["bg-team-amber", "bg-team-sapphire"] : ["bg-team-sapphire", "bg-team-amber"];
  return (
    <ToggleGroup
      type="multiple"
      variant="pill"
      aria-label={_(LABEL)}
      value={ids.filter((id) => layers[id])}
      onValueChange={(value) => {
        onChange(
          Object.fromEntries(ids.map((id) => [id, value.includes(id)])) as Record<
            MapLayer,
            boolean
          >,
        );
      }}
      className="[scrollbar-width:none] flex-nowrap overflow-x-auto @lg:flex-wrap @lg:overflow-visible"
    >
      {ids.map((id) => (
        <ToggleGroupItem key={id} value={id} data-layer={id} className="shrink-0">
          <span
            aria-hidden="true"
            className={cn(
              "size-2.5 rounded-full",
              id === "mates" ? teamDots[0] : id === "enemies" ? teamDots[1] : LAYERS[id].dot,
            )}
          />
          {_(LAYERS[id].label)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};
